/* eslint-disable no-underscore-dangle */
import $ from 'jquery';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import Player from '../models/Player';
import mediaFormats from '../constants/mediaFormats';
import htmlMediaEvents from '../constants/htmlMediaEvents';
import timeToPercentage from '../utils/timeToPercentage';
import playerName from '../constants/playerName';

const jPlayerFormatsMap = {
  [mediaFormats.mp3]: 'mp3',
  [mediaFormats.aac]: 'm4a',
  [mediaFormats.ogg]: 'oga',
  [mediaFormats.flash]: 'rtmpa',
  [mediaFormats.hls]: 'm3u8a',
};

const mediaErrorCodeMap = {
  1: 'MediaErrorAborted',
  2: 'MediaErrorNetwork',
  3: 'MediaErrorDecode',
  4: 'MediaErrorSourceNotSupported',
};

function onError(callback, mediaElement) {
  return (event) => {
    const { type, data } = event;
    const error = new Error(`${playerName.JPlayer} error`);
    const mediaError = get(mediaElement, 'error') || {};

    error.errorData = {
      type,
      data,
      message: `${mediaErrorCodeMap[mediaError.code] || 'PlayerError'}: ${
        mediaError.message || error.message
      }`,
      isMediaError: !!mediaError.code,
    };

    return callback(error);
  };
}

export default class JPlayer extends Player {
  constructor(config) {
    super(playerName.JPlayer);
    this.jPlayerLibrary = config.jPlayerInstance;
    this.playerEl = config.mediaElement;
    this.volume = null;
    this.muted = false;
    this.jPlayerEventMap = {
      [htmlMediaEvents.playing]: $.jPlayer.event.playing,
      [htmlMediaEvents.play]: $.jPlayer.event.play,
      [htmlMediaEvents.progress]: $.jPlayer.event.progress,
      [htmlMediaEvents.waiting]: $.jPlayer.event.waiting,
      [htmlMediaEvents.timeupdate]: $.jPlayer.event.timeupdate,
      [htmlMediaEvents.ended]: $.jPlayer.event.ended,
      [htmlMediaEvents.error]: $.jPlayer.event.error,
    };
  }

  on(event, callback) {
    const jPlayerEvent = this.jPlayerEventMap[event];
    if (!jPlayerEvent) {
      return;
    }

    let callbackToUse = callback;

    if (jPlayerEvent === $.jPlayer.event.error) {
      callbackToUse = onError(callback, this.playerEl);
      this.cachedErrorCallback = callbackToUse;
    }

    this.jPlayerLibrary.on(jPlayerEvent, callbackToUse);
  }

  off(event, callback) {
    const jPlayerEvent = this.jPlayerEventMap[event];
    if (!jPlayerEvent) {
      return;
    }

    let callbackToUse = callback;

    if (jPlayerEvent === $.jPlayer.event.error) {
      callbackToUse = this.cachedErrorCallback;
      delete this.cachedErrorCallback;
    }

    this.jPlayerLibrary.off(jPlayerEvent, callbackToUse);
  }

  /**
   * This is necessary on Safari mobile and desktop browsers due to autoplay restrictions.
   * See PlayerLoader.js for more details.
   * @param blankMp3Url {string}
   */
  preload(blankMp3Url) {
    this.jPlayerLibrary.jPlayer('setMedia', { mp3: blankMp3Url });
    this.jPlayerLibrary.jPlayer('play');
  }

  load(url, format) {
    const mediaFormat = jPlayerFormatsMap[format];
    const media = {
      [mediaFormat]: url,
    };

    return new Promise((resolve, reject) => {
      this.jPlayerLibrary.one($.jPlayer.event.canplay, resolve);
      // There's a bug in iOS 17.4 where `canplay` is never fired for stream urls that don't have a
      // file extension. However, the `loadedmetadata` event is fired, and we've observed that,
      // typically, `canplay` fires 10-50ms after `loadedmetadata`, even on slow 3G connections.
      // So we'll implement this fallback listener with a 150ms timeout buffer to cover this edge case.
      this.jPlayerLibrary.one($.jPlayer.event.loadedmetadata, () =>
        setTimeout(resolve, 150),
      );
      this.jPlayerLibrary.one(
        $.jPlayer.event.error,
        onError(reject, this.playerEl),
      );
      this.jPlayerLibrary.jPlayer('setMedia', media);
    });
  }

  async play(playPosition) {
    const playEvent = await new Promise((resolve, reject) => {
      this.jPlayerLibrary.one(
        $.jPlayer.event.error,
        onError(reject, this.playerEl),
      );
      this.jPlayerLibrary.one($.jPlayer.event.playing, resolve);
      this.jPlayerLibrary.jPlayer('play');
    });
    const duration = get(playEvent, 'jPlayer.status.duration');
    const percentage = timeToPercentage(playPosition, duration);
    if (!percentage) {
      return;
    }
    this.jPlayerLibrary.jPlayer('playHead', percentage);
  }

  seek(position) {
    // TUNE-9375 As late as Chrome 78, seeking to 100% causes player to hang for
    // several seconds before tuning to next topic
    this.jPlayerLibrary.jPlayer(
      'playHead',
      position === 100 ? 99.99 : position,
    );
  }

  pause() {
    this.jPlayerLibrary.jPlayer('pause');
  }

  stop() {
    this.jPlayerLibrary.jPlayer('stop');
  }

  canChangePlaybackRate() {
    return !!this.jPlayerLibrary.data('jPlayer').status.playbackRateEnabled;
  }

  getPlaybackRate() {
    return this.jPlayerLibrary.data('jPlayer').status.playbackRate;
  }

  // http://jplayer.org/latest/developer-guide/#jPlayer-option-playbackRate
  changePlaybackRate(rate) {
    this.jPlayerLibrary.jPlayer('playbackRate', rate);
  }

  unload() {
    this.jPlayerLibrary.jPlayer('clearMedia');
  }

  destroy() {
    this.jPlayerLibrary.off();
    this.jPlayerLibrary.jPlayer('clearMedia');
    this.jPlayerLibrary.jPlayer('destroy');
  }

  setVolume(volume) {
    if (isNumber(volume)) {
      this.volume = volume;
      this.jPlayerLibrary.jPlayer('volume', volume / 100);
    }
  }

  getVolume() {
    return this.volume;
  }

  toggleMute() {
    this.muted = !this.muted;
    this.jPlayerLibrary.jPlayer('mute', this.muted);
  }

  setTitle(title) {
    this.jPlayerLibrary.jPlayer('setMedia', { title });
  }

  getTimes() {
    const jPlayer = this.jPlayerLibrary.data('jPlayer');
    return {
      elapsedPercent: +jPlayer.status.currentPercentAbsolute.toFixed(6),
      elapsedSeconds: Math.floor(jPlayer.status.currentTime),
      duration: Math.floor(jPlayer.status.duration),
    };
  }
}
